/* eslint-disable eqeqeq */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContext } from '@/features/editor/context/editor-context';
import { TargetingButton } from '@/features/editor/widgets/shared/targeting-controls/targeting-button';
import { ThemesModal } from '@/features/editor/widgets/shared/modals/audience/themes-modal';
import { Theme } from '@/webapi/models';
import {
  AccountContext,
  AccountContextProps,
  isNotSpa,
  useFeatureBit,
} from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { Experience, PlacementKind } from '@/webapi/use-experience-api';

export function useThemesButton() {
  const ctx = useContext(AccountContext);
  const isEnabled =
    useFeatureBit(FeatureBit.SHOPIFY_THEMES) &&
    useFeatureBit(FeatureBit.APP_EMBED_REVERT) &&
    isNotSpa(ctx?.account?.store?.alias);

  const {
    resources: { themes, enabledOnMultipleThemes },
    experienceState: { currentExperience },
  } = useContext(EditorContext);

  const isThemesDisabled = useMemo(
    () =>
      !enabledOnMultipleThemes ||
      isEmpty(themes) ||
      !isEnabled ||
      isHydrogen(ctx) ||
      isCheckoutPage(currentExperience),
    [
      themes,
      currentExperience,
      isEnabled,
      enabledOnMultipleThemes,
      ctx?.account?.store?.alias,
    ],
  );

  return { isThemesDisabled };
}

export function ThemesButton({ isDisabled }: { isDisabled?: boolean }) {
  const {
    resources: { themes },
    experienceState: { currentExperience },
  } = useContext(EditorContext);
  if (isEmpty(themes)) {
    return null;
  }

  const { isThemesDisabled } = useThemesButton();

  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeTheme, setActiveTheme] = useState(
    getDefaultThemeName(themes, currentExperience),
  );
  const onClick = () => {
    setIsVisible(true);
  };

  useEffect(() => {
    if (!currentExperience.themeId) {
      setActiveTheme(`main`);
    } else {
      const activeTheme = themes.find(
        (t) => t.id === currentExperience.themeId,
      );
      if (activeTheme?.role === `main`) {
        setActiveTheme(`main`);
      } else if (activeTheme?.name) {
        setActiveTheme(activeTheme.name);
      }
    }
  }, [themes.length, currentExperience.themeId]);

  if (isThemesDisabled) {
    return null;
  }

  return (
    <>
      <ThemesModal
        fromRef={ref}
        isVisible={isVisible}
        themes={sorted(themes)}
        setIsVisible={setIsVisible}
      />
      <TargetingButton
        wrapperRef={ref}
        title="THEME"
        subtitle={activeTheme}
        onClick={onClick}
        isDisabled={isDisabled}
      />
    </>
  );
}

function isEmpty(themes: Array<Theme>) {
  return (themes || []).length == 0;
}

function getDefaultThemeName(
  themes: Array<Theme>,
  currentExperience: Experience,
) {
  const theme = themes?.find((t) => t.id == currentExperience.themeId);
  if (theme?.role === `main`) {
    return `main`;
  }
  return theme?.name || `main`;
}

function sorted(themes: Array<Theme>) {
  function customSort(a, b) {
    if (a.id === b.role) {
      return -1;
    }
    if (b.id === b.role) {
      return 1;
    }
    return new Date(a.updated_at).valueOf() - new Date(b.updated_at).valueOf(); // Sort by date for other elements
  }
  return themes.sort(customSort).reverse();
}

function isHydrogen(ctx: AccountContextProps) {
  return (
    ctx?.account?.store?.alias === `KARMA` ||
    ctx?.account?.store?.alias === `UNDEROUTFIT`
  );
}

function isCheckoutPage(x: Experience) {
  return x?.targeting?.placement?.kind === PlacementKind.CHECKOUT;
}
